import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../../lib/api";

const input = "w-full px-3 py-2 text-sm border border-stone-300 rounded-md bg-white focus:outline-none focus:border-bronze-dark";
const EMPTY = { patient_name: "", email: "", items: [], custom: [], note: "", expiry_hours: 168 };

export const PaymentRequestForm = ({ onCreated }) => {
  const [prices, setPrices] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/payment-requests/prices").then(({ data }) => setPrices(data.items)); }, []);
  const toggle = (k) => setF({ ...f, items: f.items.includes(k) ? f.items.filter((x) => x !== k) : [...f.items, k] });
  const setCustom = (i, k, v) => setF({ ...f, custom: f.custom.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  const total = prices.filter((p) => f.items.includes(p.key)).reduce((a, p) => a + p.pence / 100, 0) + f.custom.reduce((a, c) => a + (Number(c.amount) || 0), 0);

  const submit = async (sendEmail) => {
    setBusy(true);
    try {
      const body = { ...f, email: f.email || null, note: f.note || null, send_email: sendEmail, custom: f.custom.map((c) => ({ label: c.label, amount: Number(c.amount) })) };
      const { data } = await api.post("/admin/payment-requests", body);
      if (sendEmail) toast[data.emailed ? "success" : "error"](data.emailed ? `Payment link emailed to ${data.email}.` : "Link created, but the email couldn't be sent.");
      setF(EMPTY);
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  return (
    <div data-testid="payreq-form" className="bg-white border border-stone-200 rounded-md p-6 space-y-4">
      <h2 className="font-serif text-2xl">New payment request</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={input} placeholder="Patient full name" value={f.patient_name} onChange={(e) => setF({ ...f, patient_name: e.target.value })} data-testid="payreq-name" />
        <input className={input} type="email" placeholder="Patient email (to send the link)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="payreq-email" />
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {prices.map((p) => (
          <label key={p.key} className="flex items-center gap-2 text-sm p-2 border border-stone-200 rounded-md cursor-pointer" data-testid={`payreq-item-${p.key}`}>
            <input type="checkbox" checked={f.items.includes(p.key)} onChange={() => toggle(p.key)} className="accent-[#8C6643]" />
            <span className="flex-1">{p.label}</span><span className="text-ink-muted">£{(p.pence / 100).toFixed(2)}</span>
          </label>
        ))}
      </div>
      {f.custom.map((c, i) => (
        <div key={i} className="flex gap-2" data-testid={`payreq-custom-${i}`}>
          <input className={input} placeholder="What it's for" value={c.label} onChange={(e) => setCustom(i, "label", e.target.value)} data-testid={`payreq-custom-label-${i}`} />
          <input className={`${input} w-32`} type="number" min="1" step="0.01" placeholder="£" value={c.amount} onChange={(e) => setCustom(i, "amount", e.target.value)} data-testid={`payreq-custom-amount-${i}`} />
          <button type="button" aria-label="Remove" onClick={() => setF({ ...f, custom: f.custom.filter((_, j) => j !== i) })}><Trash size={18} /></button>
        </div>
      ))}
      <button type="button" onClick={() => setF({ ...f, custom: [...f.custom, { label: "", amount: "" }] })} className="text-sm text-bronze-dark inline-flex items-center gap-1" data-testid="payreq-add-custom"><Plus size={14} /> Add another amount</button>
      <textarea className={input} rows={2} maxLength={300} placeholder="Note for the patient (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="payreq-note" />
      <div className="flex flex-wrap items-center gap-3">
        <select className="px-3 py-2 text-sm border border-stone-300 rounded-md bg-white" value={f.expiry_hours} onChange={(e) => setF({ ...f, expiry_hours: Number(e.target.value) })} data-testid="payreq-expiry">
          <option value={168}>Link works for 7 days</option><option value={24}>Link works for 24 hours</option>
        </select>
        <span className="font-medium" data-testid="payreq-total">Total £{total.toFixed(2)}</span>
        <button type="button" disabled={busy} onClick={() => submit(false)} className="btn-primary ml-auto disabled:opacity-50" data-testid="payreq-create">Create link</button>
        <button type="button" disabled={busy || !f.email} onClick={() => submit(true)} className="btn-secondary disabled:opacity-40" data-testid="payreq-create-email">Create &amp; email</button>
      </div>
    </div>
  );
};
